class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = null;
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Find the container - try immediately and also in next tick
    this.findResultsContainer();
    if (!this.predictiveSearchResults) {
      setTimeout(() => this.findResultsContainer(), 0);
    }

    // Find popular searches container and save its original HTML
    this.popularSearchesContainer = this.querySelector('[data-popular-searches]');
    if (this.popularSearchesContainer && this.predictiveSearchResults) {
      // Save the original HTML including the popular searches container
      this.originalPopularSearchesHTML = this.popularSearchesContainer.outerHTML;
    }

    this.setupEventListeners();
    this.setupPopularSearchesListeners();
  }

  findResultsContainer() {
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    if (!this.predictiveSearchResults) {
      console.warn('PredictiveSearch: [data-predictive-search] container not found');
    }
  }

  setupPopularSearchesListeners() {
    if (!this.popularSearchesContainer) {
      // Try to find it again in case it wasn't available during construction
      this.popularSearchesContainer = this.querySelector('[data-popular-searches]');
      if (this.popularSearchesContainer && !this.originalPopularSearchesHTML) {
        this.originalPopularSearchesHTML = this.popularSearchesContainer.outerHTML;
      }
    }

    if (!this.popularSearchesContainer) return;

    // Handle clicks on popular search items
    const popularSearchButtons = this.popularSearchesContainer.querySelectorAll('[data-popular-search]');
    popularSearchButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const searchTerm = button.getAttribute('data-popular-search');
        if (this.input && searchTerm) {
          this.input.value = searchTerm;
          this.onChange();
        }
      });
    });
  }

  setupEventListeners() {
    if (!this.input || !this.input.form) {
      return;
    }

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.input.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onInput(event) {
    // Handle real-time input changes
    this.onChange();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Clear previous results
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
      this.hideSeeAllResultsButton();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      // Restore popular searches when input is empty
      this.restorePopularSearches();
      this.close(true);
      return;
    }

    // Hide popular searches when user starts typing
    this.hidePopularSearches();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Show popular searches when input is focused and empty
      this.restorePopularSearches();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    // Don't close in drawer context
    const isInDrawer = this.closest('.search-drawer') || this.closest('search-drawer');
    if (isInDrawer) {
      return;
    }

    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      // Restore popular searches when input is cleared via backspace
      this.restorePopularSearches();
      this.close(true);
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) {
      this.statusElement.textContent = '';
    }

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) {
      selectedOption.click();
    }
  }

  getSearchResults(searchTerm) {
    if (!searchTerm || !searchTerm.trim()) {
      return;
    }

    // Ensure container is found before proceeding
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
      if (!this.predictiveSearchResults) {
        console.error('PredictiveSearch: Cannot search - container not found');
        return;
      }
    }

    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Use Section Rendering API to get HTML from predictive-search.liquid
    const sectionUrl = `/search?section_id=predictive-search&type=product&q=${encodeURIComponent(searchTerm)}`;
    
    console.log('Fetching section from:', sectionUrl);
    
    fetch(sectionUrl, {
      signal: this.abortController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((html) => {
        console.log('Received HTML response, length:', html.length);
        
        // Parse the HTML response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract the section content - Shopify wraps it in #shopify-section-{section-name}
        const sectionElement = doc.querySelector('#shopify-section-predictive-search');
        
        if (sectionElement) {
          // Get the innerHTML - this is what's rendered from predictive-search.liquid
          const resultsMarkup = sectionElement.innerHTML.trim();
          console.log('Extracted section HTML, length:', resultsMarkup.length);
          
          if (resultsMarkup.length === 0) {
            console.log('Section HTML is empty');
            this.showNoResults();
            return;
          }
          
          // Cache the results
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          
          // Inject the rendered HTML from liquid template
          this.renderSearchResults(resultsMarkup);
        } else {
          console.log('Section element not found in response');
          this.showNoResults();
        }
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          return;
        }
        console.error('Predictive search error:', error);
        this.showNoResults();
      });
  }

  showNoResults() {
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
      if (!this.predictiveSearchResults) {
        console.error('Cannot show no results: container not found');
        return;
      }
    }
    
    const noResultsMarkup = '<p class="text-center py-8 text-primary-dark font-manrope text-sm">No products found</p>';
    this.predictiveSearchResults.innerHTML = noResultsMarkup;
    this.setAttribute('results', true);
    this.open();
    this.hideSeeAllResultsButton();
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    // Only set live region text if statusElement exists
    if (this.statusElement && this.loadingText) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);

    // Show loading indicator
    this.showLoadingIndicator();
  }

  showLoadingIndicator() {
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
      if (!this.predictiveSearchResults) {
        return;
      }
    }
    
    // Create loading HTML if it doesn't exist
    const loadingHTML = `
      <div class="predictive-search-loading flex items-center justify-center py-12">
        <div class="predictive-search-spinner"></div>
      </div>
    `;
    
    this.predictiveSearchResults.innerHTML = loadingHTML;
    this.open();
  }

  setLiveRegionText(statusText) {
    // Add null check to prevent errors
    if (!this.statusElement || !statusText) {
      return;
    }

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      if (this.statusElement) {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
      if (!this.predictiveSearchResults) {
        console.error('Cannot render results: container not found');
        return;
      }
    }

    // Hide popular searches when showing results
    this.hidePopularSearches();

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    
    const hasPredictiveItems = this.predictiveSearchResults.querySelector(
      'ul.predictive-search-results-list li.predictive-search-item'
    );
    if (hasPredictiveItems) {
      this.showSeeAllResultsButton();
    } else {
      this.hideSeeAllResultsButton();
    }
  }

  showSeeAllResultsButton() {
    // Find the button in the drawer context
    const isInDrawer = this.closest('.search-drawer') || this.closest('search-drawer');
    if (isInDrawer) {
      const seeAllButton = document.querySelector('[data-see-all-results-button]');
      if (seeAllButton) {
        seeAllButton.classList.remove('hidden');
      }
    }
  }

  hideSeeAllResultsButton() {
    const seeAllButton = document.querySelector('[data-see-all-results-button]');
    if (seeAllButton) {
      seeAllButton.classList.add('hidden');
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  restorePopularSearches() {
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
    }

    if (!this.predictiveSearchResults) return;

    // Restore the original popular searches HTML
    if (this.originalPopularSearchesHTML) {
      this.predictiveSearchResults.innerHTML = this.originalPopularSearchesHTML;
      // Re-find the container after restoring HTML
      this.popularSearchesContainer = this.querySelector('[data-popular-searches]');
      // Re-setup event listeners for the restored buttons
      this.setupPopularSearchesListeners();
      // Ensure the results container is visible
      this.predictiveSearchResults.style.display = 'block';
    } else {
      // Fallback: just show if container exists
      this.showPopularSearches();
    }
    this.hideSeeAllResultsButton();
  }

  showPopularSearches() {
    if (!this.popularSearchesContainer) {
      this.popularSearchesContainer = this.querySelector('[data-popular-searches]');
    }
    
    if (this.popularSearchesContainer) {
      this.popularSearchesContainer.style.display = 'block';
      // Ensure the results container is visible
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.style.display = 'block';
      }
    }
  }

  hidePopularSearches() {
    if (this.popularSearchesContainer) {
      this.popularSearchesContainer.style.display = 'none';
    }
  }

  getResultsMaxHeight() {
    // Check if we're in a drawer context
    const isInDrawer = this.closest('.search-drawer') || this.closest('search-drawer');
    
    if (isInDrawer) {
      const drawer = this.closest('.search-drawer') || this.closest('search-drawer');
      if (drawer) {
        const drawerHeight = drawer.offsetHeight;
        const inputHeight = this.input?.offsetHeight || 50;
        const padding = 100;
        return drawerHeight - inputHeight - padding;
      }
    }

    this.resultsMaxHeight =
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.predictiveSearchResults) {
      this.findResultsContainer();
      if (!this.predictiveSearchResults) {
        return;
      }
    }

    // Check if we're in a drawer context
    const isInDrawer = this.closest('.search-drawer') || this.closest('search-drawer');
    
    if (!isInDrawer) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    } else {
      this.predictiveSearchResults.style.display = 'block';
    }
    
    this.setAttribute('open', true);
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = '';
      }
      this.removeAttribute('results');
      // Restore popular searches when search is cleared
      this.restorePopularSearches();
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute('aria-selected', false);
    }

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);